import { useState, useRef, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';

const getStartErrorMessage = (error, mediaType) => {
  const device = mediaType === 'video' ? 'camera and microphone' : 'microphone';

  switch (error?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return `Access to your ${device} is blocked. Allow it from the icon at the right of your browser's address bar, then try again.`;
    case 'NotFoundError':
    case 'OverconstrainedError':
      return `No ${device} was found. Connect one and try again.`;
    case 'NotReadableError':
      return `Your ${device} is already in use by another app. Close it and try again.`;
    default:
      return error?.message || `Could not start the ${mediaType} recording.`;
  }
};

export const useMediaRecorder = ({ mediaType, maxDurationSeconds }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [mediaBlob, setMediaBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [startError, setStartError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const toast = useToast();

  const getSupportedMimeType = () => {
    if (mediaType === 'audio') {
      // Audio fallback
      const audioTypes = ['audio/webm', 'audio/mp4', 'audio/mpeg'];
      for (const type of audioTypes) {
        if (MediaRecorder.isTypeSupported(type)) return type;
      }
      return '';
    }
    
    // priority based fallback for video
    const types = [
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm',
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return '';
  };

  // Resolves to true only once the recorder is actually running. Callers rely on this to
  // roll back any "busy" state they claimed optimistically before asking for permission.
  const startRecording = useCallback(async () => {
    setStartError(null);
    try {
      const constraints = mediaType === 'video' 
        ? { video: true, audio: true }
        : { audio: true };
        
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const options = mimeType ? { mimeType } : undefined;
      
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      mediaChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          mediaChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const type = mimeType || (mediaType === 'video' ? 'video/webm' : 'audio/webm');
        const blob = new Blob(mediaChunksRef.current, { type });
        setMediaBlob(blob);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start(1000); // chunk every 1 second
      setIsRecording(true);
      setDuration(0);
      setMediaBlob(null);

      // Start timer
      timerRef.current = setInterval(() => {
        setDuration((prev) => {
          if (prev >= maxDurationSeconds - 1) {
            stopRecording();
            toast({
              title: 'Recording stopped',
              description: `Maximum duration of ${maxDurationSeconds}s reached.`,
              status: 'warning',
              duration: 3000,
              isClosable: true,
            });
            return maxDurationSeconds;
          }
          return prev + 1;
        });
      }, 1000);

      return true;
    } catch (error) {
      console.error('Error accessing media devices:', error);

      // getUserMedia may have succeeded before a later step threw — never leave the
      // device light on.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      mediaRecorderRef.current = null;
      setIsRecording(false);
      clearInterval(timerRef.current);

      const message = getStartErrorMessage(error, mediaType);
      setStartError(message);
      toast({
        title: 'Cannot start recording',
        description: message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      return false;
    }
  }, [mediaType, maxDurationSeconds, toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  }, []);

  const resetRecording = useCallback(() => {
    setMediaBlob(null);
    setDuration(0);
    setStartError(null);
    mediaChunksRef.current = [];
  }, []);

  return {
    isRecording,
    mediaBlob,
    duration,
    startError,
    startRecording,
    stopRecording,
    resetRecording,
    stream: streamRef.current
  };
};
